import type { SSOContext } from '@frontend/web-react';
import type { Reducer } from 'react';

export type SSOCallbackStep =
  | 'checkingTransfer'
  | 'completingAuth'
  | 'error'
  | 'initializing'
  | 'missingRequirements'
  | 'navigating'
  | 'processingSession'
  | 'transferring';

export type SSOCallbackState = {
  error: string | null;
  hasProcessedSession: boolean;
  hasStartedTransfer: boolean;
  isSubmittingProfile: boolean;
  step: SSOCallbackStep;
};

export type TransferDirection = 'toSignIn' | 'toSignUp';

export type SSOCallbackAction =
  | { direction: TransferDirection; type: 'needsTransfer' }
  | { error: string; type: 'error' }
  | { error: string; type: 'transferFailed' }
  | { type: 'authComplete' }
  | { type: 'navigateToMCPComplete' }
  | { type: 'profileSubmitEnd' }
  | { type: 'profileSubmitStart' }
  | { type: 'sessionReady' }
  | { type: 'showProfileForm' }
  | { type: 'ssoLoaded' }
  | { type: 'startAuthCompletion' }
  | { type: 'timeout' }
  | { type: 'transferComplete' };

export type TransferCheck = {
  direction?: TransferDirection;
  needsTransfer: boolean;
};

const initialState: SSOCallbackState = {
  error: null,
  hasProcessedSession: false,
  hasStartedTransfer: false,
  isSubmittingProfile: false,
  step: 'initializing',
};

export const checkTransferNeeded = (signIn: SSOContext['signIn'], signUp: SSOContext['signUp']): TransferCheck => {
  const signUpVerification = signUp?.verifications?.externalAccount;
  if (signUpVerification?.status === 'transferable' && signUpVerification?.error?.code === 'external_account_exists') {
    return { direction: 'toSignIn', needsTransfer: true };
  }

  const signInVerification = signIn?.firstFactorVerification;
  if (
    signInVerification?.status === 'transferable' &&
    signInVerification?.error?.code === 'external_account_not_found'
  ) {
    return { direction: 'toSignUp', needsTransfer: true };
  }

  return { needsTransfer: false };
};

export const ssoCallbackStateMachine: Reducer<SSOCallbackState, SSOCallbackAction> = (prev, action) => {
  switch (prev.step) {
    case 'initializing':
      if (action.type === 'ssoLoaded') {
        return { ...prev, step: 'checkingTransfer' };
      }
      break;

    case 'checkingTransfer':
      if (action.type === 'needsTransfer' && !prev.hasStartedTransfer) {
        return { ...prev, hasStartedTransfer: true, step: 'transferring' };
      } else if (action.type === 'sessionReady' && !prev.hasProcessedSession) {
        return { ...prev, hasProcessedSession: true, step: 'processingSession' };
      } else if (action.type === 'timeout') {
        return { ...prev, error: 'Unable to establish connection.', step: 'error' };
      }
      break;

    case 'transferring':
      if (action.type === 'transferComplete') {
        return { ...prev, step: 'checkingTransfer' };
      } else if (action.type === 'transferFailed') {
        return { ...prev, error: action.error, step: 'error' };
      }
      break;

    case 'processingSession':
      if (action.type === 'navigateToMCPComplete') {
        return { ...prev, step: 'navigating' };
      } else if (action.type === 'showProfileForm') {
        return { ...prev, step: 'missingRequirements' };
      } else if (action.type === 'startAuthCompletion') {
        return { ...prev, step: 'completingAuth' };
      }
      break;

    case 'completingAuth':
      if (action.type === 'authComplete') {
        return { ...prev, step: 'navigating' };
      } else if (action.type === 'error') {
        return { ...prev, error: action.error, step: 'error' };
      }
      break;

    case 'missingRequirements':
      if (action.type === 'profileSubmitStart') {
        return { ...prev, isSubmittingProfile: true };
      } else if (action.type === 'profileSubmitEnd') {
        return { ...prev, isSubmittingProfile: false };
      } else if (action.type === 'authComplete') {
        return { ...prev, step: 'navigating' };
      } else if (action.type === 'error') {
        return { ...prev, error: action.error, step: 'error' };
      }
      break;

    case 'error':
    case 'navigating':
      break;

    default:
      break;
  }

  return prev;
};

export { initialState as ssoCallbackInitialState };
